/**
 * Simple test utility for Supabase operations
 *
 * To run tests, add this to any component temporarily:
 *   import { runSupabaseTests } from './lib/testSupabase';
 *   useEffect(() => { runSupabaseTests(); }, []);
 *
 * Or call window.testSupabase() from browser console after the app loads.
 */

import { supabase } from './supabase';

// Expose to window for console access
if (typeof window !== 'undefined') {
  window.testSupabase = async () => {
    try {
      const results = await runTests();
      return results;
    } catch (err) {
      console.error('Test suite error:', err);
      return { error: err.message };
    }
  };

  // Quick connection test
  window.testSupabaseConnection = async () => {
    console.log('Testing Supabase connection...');
    try {
      const { data, error } = await supabase.auth.getSession();
      if (error) {
        console.error('Connection error:', error);
        return { connected: false, error: error.message };
      }
      console.log('Connection successful!', data.session ? `Logged in as: ${data.session.user.email}` : 'Not logged in');
      return { connected: true, session: data.session };
    } catch (e) {
      console.error('Connection failed:', e);
      return { connected: false, error: e.message };
    }
  };
}

export const runTests = async () => {
  console.log('🧪 Running Supabase connection tests...');
  console.log('Supabase URL:', process.env.REACT_APP_SUPABASE_URL ? 'Set' : 'MISSING');
  console.log('Supabase Anon Key:', process.env.REACT_APP_SUPABASE_ANON_KEY ? 'Set' : 'MISSING');

  const results = {
    passed: 0,
    failed: 0,
    tests: []
  };

  // Test 1: Check auth session
  console.log('Test 1: Checking auth session...');
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    if (!session) {
      results.tests.push({ name: 'Auth Session', status: 'SKIP', message: 'Not logged in' });
      console.log('⏭️ Auth Session: SKIP - Not logged in');
    } else {
      results.tests.push({ name: 'Auth Session', status: 'PASS', message: `User: ${session.user.email}` });
      results.passed++;
      console.log('✅ Auth Session: PASS - User:', session.user.email);
    }
  } catch (e) {
    results.tests.push({ name: 'Auth Session', status: 'FAIL', message: e.message });
    results.failed++;
    console.log('❌ Auth Session: FAIL -', e.message);
  }

  // Get current user for subsequent tests
  console.log('Getting current session for authenticated tests...');
  const { data: { session } } = await supabase.auth.getSession();
  const userId = session?.user?.id;

  if (!userId) {
    console.log('⚠️ Not logged in - skipping authenticated tests');
    printResults(results);
    return results;
  }

  // Test 2: Read user profile
  console.log('Test 2: Reading user profile...');
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('id, email, tier')
      .eq('id', userId)
      .single();
    if (error) throw error;
    results.tests.push({ name: 'Read Profile', status: 'PASS', message: `Tier: ${data.tier}` });
    results.passed++;
    console.log('✅ Read Profile: PASS - Tier:', data.tier);
  } catch (e) {
    results.tests.push({ name: 'Read Profile', status: 'FAIL', message: e.message });
    results.failed++;
    console.log('❌ Read Profile: FAIL -', e.message);
  }

  // Test 3: Insert bug report
  console.log('Test 3: Inserting test bug report...');
  try {
    const { error } = await supabase
      .from('bug_reports')
      .insert({
        user_id: userId,
        title: 'Test bug report',
        description: 'This is a test - please ignore',
        severity: 'low',
        category: 'other'
      });
    if (error) throw error;
    results.tests.push({ name: 'Insert Bug Report', status: 'PASS', message: 'Insert successful' });
    results.passed++;
    console.log('✅ Insert Bug Report: PASS');
  } catch (e) {
    results.tests.push({ name: 'Insert Bug Report', status: 'FAIL', message: e.message });
    results.failed++;
    console.log('❌ Insert Bug Report: FAIL -', e.message);
  }

  // Test 4: Insert saved itinerary
  console.log('Test 4: Inserting test itinerary...');
  try {
    const testItinerary = { days: [], destination: 'Test', test: true };
    const { error } = await supabase
      .from('saved_itineraries')
      .insert({
        user_id: userId,
        title: 'Test Itinerary',
        destination: 'Test City',
        itinerary_data: testItinerary,
        status: 'saved'
      });
    if (error) throw error;
    results.tests.push({ name: 'Insert Itinerary', status: 'PASS', message: 'Insert successful' });
    results.passed++;
    console.log('✅ Insert Itinerary: PASS');
  } catch (e) {
    results.tests.push({ name: 'Insert Itinerary', status: 'FAIL', message: e.message });
    results.failed++;
    console.log('❌ Insert Itinerary: FAIL -', e.message);
  }

  // Test 5: Read saved itineraries
  console.log('Test 5: Reading saved itineraries...');
  try {
    const { data, error } = await supabase
      .from('saved_itineraries')
      .select('id, title')
      .eq('user_id', userId);
    if (error) throw error;
    results.tests.push({ name: 'Read Itineraries', status: 'PASS', message: `Found ${data.length} itineraries` });
    results.passed++;
    console.log('✅ Read Itineraries: PASS - Found', data.length, 'itineraries');
  } catch (e) {
    results.tests.push({ name: 'Read Itineraries', status: 'FAIL', message: e.message });
    results.failed++;
    console.log('❌ Read Itineraries: FAIL -', e.message);
  }

  // Test 6: Usage tracking (via RPC function)
  console.log('Test 6: Testing usage tracking RPC...');
  try {
    const { data, error } = await supabase.rpc('get_monthly_usage', { p_user_id: userId });
    if (error) throw error;
    results.tests.push({ name: 'Usage Tracking', status: 'PASS', message: `Itineraries: ${data[0]?.itineraries_generated || 0}` });
    results.passed++;
    console.log('✅ Usage Tracking: PASS - Itineraries:', data[0]?.itineraries_generated || 0);
  } catch (e) {
    results.tests.push({ name: 'Usage Tracking', status: 'FAIL', message: e.message });
    results.failed++;
    console.log('❌ Usage Tracking: FAIL -', e.message);
  }

  printResults(results);
  return results;
};

const printResults = (results) => {
  console.log('\n📊 Test Results:');
  console.log('─'.repeat(50));

  results.tests.forEach(test => {
    const icon = test.status === 'PASS' ? '✅' : test.status === 'FAIL' ? '❌' : '⏭️';
    console.log(`${icon} ${test.name}: ${test.status}`);
    if (test.message) console.log(`   ${test.message}`);
  });

  console.log('─'.repeat(50));
  console.log(`Total: ${results.passed} passed, ${results.failed} failed`);

  if (results.failed > 0) {
    console.log('\n🔧 To fix RLS issues, run the schema.sql in Supabase SQL Editor');
  }
};

export const runSupabaseTests = runTests;
export default { runTests, runSupabaseTests };
